import type { RouletteSpinResult } from '../_hooks/useRouletteSpin';

export type RouletteAvailability =
  'DRAWABLE' | 'INSUFFICIENT_BALANCE' | 'NO_ITEM' | 'UNRECOVERED';

interface RouletteAvailabilitySource {
  balance?: number | null;
  requiredPoint: number;
  blankProbability?: number | null;
  unrecoveredExistsYN?: boolean | null;
}

//* blankProbability가 null이면 뽑을 칸이 없다는 서버 계약이다 — 잔액과 무관하게 먼저 가른다.
export const resolveRouletteAvailability = ({
  balance,
  requiredPoint,
  blankProbability,
  unrecoveredExistsYN,
}: RouletteAvailabilitySource): RouletteAvailability => {
  if (typeof blankProbability !== 'number') {
    return 'NO_ITEM';
  }

  if (unrecoveredExistsYN === true) {
    return 'UNRECOVERED';
  }

  if (typeof balance === 'number' && balance < requiredPoint) {
    return 'INSUFFICIENT_BALANCE';
  }

  return 'DRAWABLE';
};

export type RouletteRetryType = 'FREE' | 'AD' | 'NONE';

interface RouletteSpinSource {
  won: boolean;
  entryClosed: boolean;
  nextRetryType: RouletteRetryType;
}

//* 재도전 종류는 서버가 직전 응답(nextRetryType)으로 정한다 — 회차로 추론하지 않는다.
export const resolveRouletteRetryType = ({
  won,
  entryClosed,
  nextRetryType,
}: RouletteSpinSource): RouletteRetryType => {
  if (won || entryClosed) {
    return 'NONE';
  }

  return nextRetryType;
};

interface RouletteLandingItem {
  blankYN: boolean;
  name?: string | null;
  imageUrl?: string | null;
}

interface RouletteLandingResult {
  won: boolean;
  resultItemName?: string | null;
  resultImageUrl?: string | null;
}

//* 응답은 칸 ID를 주지 않아 멈출 칸을 이름·이미지로 찾는다 — 못 찾으면 -1(멈춤 연출 생략).
export const resolveRouletteLandingIndex = (
  items: RouletteLandingItem[],
  { won, resultItemName, resultImageUrl }: RouletteLandingResult,
) => {
  if (!won) {
    return items.findIndex((item) => item.blankYN);
  }

  const imageMatchIndex = resultImageUrl
    ? items.findIndex(
        (item) => !item.blankYN && item.imageUrl === resultImageUrl,
      )
    : -1;

  if (imageMatchIndex >= 0) {
    return imageMatchIndex;
  }

  return items.findIndex(
    (item) => !item.blankYN && item.name === resultItemName,
  );
};

interface RouletteInProgressEntry {
  rouletteEntryId: string;
  remainingRetryCount: number;
  nextRetryType: RouletteRetryType;
  retryDeadlineAt?: string;
}

//* 재진입 화면은 직전 회차 결과 없이 시작하므로 진행 중 참여를 미당첨 회차 결과로 흉내 낸다 — 이어 돌릴 종류는 서버 값 그대로.
export const createResumedRouletteSpin = ({
  rouletteEntryId,
  remainingRetryCount,
  nextRetryType,
}: RouletteInProgressEntry): RouletteSpinResult => ({
  rouletteEntryId,
  remainingRetryCount,
  nextRetryType,
  won: false,
  entryClosed: false,
  round: 1,
});
